import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";
import { LogOut, ShoppingBag, LayoutDashboard, Sparkles, Menu, X, ArrowUpRight, UserCircle } from "lucide-react";
import { useState, useEffect } from "react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/order", label: "Order" },
  { to: "/ai-designer", label: "AI Designer" },
  { to: "/services", label: "Services" },
  { to: "/track", label: "Track" },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "pt-3" : "pt-5"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="pav-pill-nav flex items-center justify-between gap-3 px-4 py-2.5 lg:px-5">
          <Link to="/" className="flex items-center gap-2.5 pl-1" data-testid="logo-home-link">
            <img src={LOGO_URL} alt="Print Ad Venture" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/20" />
            <div className="leading-none hidden sm:block text-white">
              <div className="font-display font-extrabold text-sm tracking-tight">PRINT AD VENTURE</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-white/55 mt-0.5">Tarpaulin & Large Format</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-medium rounded-full transition-all ${
                    isActive ? "bg-white text-[#0B1E3F]" : "text-white/80 hover:text-white hover:bg-white/8"
                  }`
                }>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-button"
                    className="flex items-center gap-2 bg-white/8 hover:bg-white/14 transition-colors px-2.5 py-1.5 rounded-full">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-[#06B6D4] flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                    <span className="hidden sm:block text-sm text-white pr-1.5">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/orders")} data-testid="menu-my-orders">
                    <ShoppingBag className="mr-2 h-4 w-4" /> My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/profile")} data-testid="menu-my-profile">
                    <UserCircle className="mr-2 h-4 w-4" /> My Profile
                  </DropdownMenuItem>
                  {(user.role === "admin" || user.role === "staff") && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                        <Sparkles className="mr-2 h-4 w-4" /> Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" data-testid="header-login-link"
                  className="px-3 py-1.5 text-sm font-medium text-white/85 hover:text-white">
                  Sign in
                </Link>
                <Link to="/register" data-testid="header-register-link"
                  className="pav-pill-btn text-sm">
                  Get started
                  <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
                </Link>
              </div>
            )}

            <button data-testid="mobile-menu-toggle"
              className="lg:hidden ml-1 p-2 rounded-full hover:bg-white/8 text-white"
              onClick={() => setOpen((v) => !v)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden mt-2 pav-pill-nav p-3" data-testid="mobile-menu" style={{ borderRadius: "1.5rem" }}>
            <div className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "bg-white text-[#0B1E3F]" : "text-white/85 hover:bg-white/8"}`
                  }>
                  {l.label}
                </NavLink>
              ))}
              {!user && (
                <div className="flex gap-2 pt-2 mt-2 border-t border-white/10">
                  <Link to="/login" onClick={() => setOpen(false)}
                    className="flex-1 px-3 py-2 rounded-full text-center text-white/85 text-sm">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)}
                    className="pav-pill-btn flex-1 text-sm justify-center">Get started
                    <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
